import React, { useState, useCallback } from 'react';
import { ShieldCheck, Activity, AlertTriangle, LogOut } from 'lucide-react';
import BootTerminal from './components/boot/BootTerminal';
import AuthShell from './components/auth/AuthShell';
import DashboardShell from './components/dashboard/DashboardShell';
import { authService } from './services/api';

export default function App() {
  // Boot Sequence State: 'booting' | 'exiting' | 'completed'
  const [bootState, setBootState] = useState(() => {
    if (typeof window !== 'undefined' && sessionStorage.getItem('titan_has_booted') === 'true') {
      return 'completed';
    }
    return 'booting';
  });

  // Auth & Session State
  const [authToken, setAuthToken] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('titan_auth_token');
    }
    return null;
  });

  const [currentUser, setCurrentUser] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('titan_auth_user');
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    }
    return null;
  });

  // Global Alert Message Banner (Rate limiting / Brute force warnings)
  const [alertMessage, setAlertMessage] = useState(null);

  // Transition from Boot to Auth/Workspace upon boot complete
  const handleBootComplete = useCallback(() => {
    setBootState('completed');
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('titan_has_booted', 'true');
    }
  }, []);

  const handleLoginSuccess = (token, user) => {
    setAuthToken(token);
    setCurrentUser(user);
    if (typeof window !== 'undefined') {
      localStorage.setItem('titan_auth_token', token);
      localStorage.setItem('titan_auth_user', JSON.stringify(user));
    }
    setAlertMessage(null);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore network errors on logout
    } finally {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('titan_auth_token');
        localStorage.removeItem('titan_auth_user');
      }
      setAuthToken(null);
      setCurrentUser(null);
    }
  };

  return (
    <div
      className={`relative bg-obsidian-void text-slate-100 flex flex-col font-sans selection:bg-neon-cyan/20 selection:text-neon-cyan ${
        authToken ? 'h-screen max-h-screen overflow-hidden' : 'min-h-screen'
      }`}
    >
      {/* Layer 0: Ambient Cyberpunk Atmosphere */}
      <div className="cyber-grid" aria-hidden="true" />
      <div className="ambient-glow-cyan" aria-hidden="true" />
      <div className="ambient-glow-magenta" aria-hidden="true" />
      <div className="cyber-scanlines" aria-hidden="true" />

      {/* Layer 1: Ultra-Slim Top HUD Status Bar */}
      <header className="relative z-20 w-full border-b border-white/[0.06] bg-obsidian-surface/80 backdrop-blur-md px-3 py-1 shrink-0">
        <div className="w-full flex items-center justify-between text-[11px] font-mono">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-emerald opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-neon-emerald"></span>
            </span>
            <span className="text-neon-emerald font-semibold uppercase tracking-wider">SYSTEM 100% OPERATIONAL</span>
            <span className="text-slate-500 hidden sm:inline">|</span>
            <span className="text-slate-400 hidden sm:inline">HYPERGRAVITY AI ENGINE: ONLINE</span>
          </div>

          <div className="flex items-center gap-4 text-slate-400">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3 h-3 text-neon-cyan" />
              <span>LATENCY: <strong className="text-neon-cyan">14ms</strong></span>
            </div>
            <div className="hidden md:flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-neon-emerald" />
              <span>RATE-SHIELD: ACTIVE</span>
            </div>
            {authToken && (
              <button
                type="button"
                onClick={handleLogout}
                className="text-neon-amber hover:text-neon-red transition-colors ml-2 font-mono underline cursor-pointer flex items-center gap-1"
              >
                <LogOut className="w-3 h-3" />
                <span>LOGOUT</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Global Alert Notification Banner */}
      {alertMessage && (
        <div className="relative z-30 max-w-4xl mx-auto mt-2 px-4 w-full animate-fade-in shrink-0">
          <div className="glass-card border-neon-red/50 bg-neon-red/10 p-2.5 rounded-lg flex items-center gap-3 text-xs text-neon-red shadow-neon-red/20">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <div className="flex-1 font-mono">{alertMessage}</div>
            <button
              type="button"
              onClick={() => setAlertMessage(null)}
              className="text-slate-400 hover:text-white px-2 py-0.5 font-mono text-xs cursor-pointer"
            >
              DISMISS
            </button>
          </div>
        </div>
      )}

      {/* Layer 2: Main Workspace Shell (Fills remaining 100vh without page scroll) */}
      <main className="relative z-10 flex-1 min-h-0 overflow-hidden flex flex-col justify-start items-stretch w-full">
        {!authToken ? (
          <AuthShell
            onAuthSuccess={handleLoginSuccess}
            onErrorBanner={(msg) => setAlertMessage(msg)}
          />
        ) : (
          <DashboardShell
            user={currentUser}
            token={authToken}
            onLogout={handleLogout}
          />
        )}
      </main>

      {/* Layer 3: Interactive Boot Sequence Overlay */}
      {bootState !== 'completed' && (
        <div
          className={`fixed inset-0 z-50 transition-all duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
            bootState === 'exiting' ? 'opacity-0 scale-[0.96] blur-[8px] pointer-events-none' : 'opacity-100 scale-100'
          }`}
        >
          <BootTerminal onComplete={handleBootComplete} />
        </div>
      )}

      {/* Footer System Telemetry (Shown only on Login/Auth screen so Trading Desk fits 100% in 1 viewport) */}
      {!authToken && (
        <footer className="relative z-10 w-full border-t border-white/[0.04] bg-obsidian-void/80 py-2.5 px-4 text-center text-xs font-mono text-slate-500 shrink-0">
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2">
            <div>WIN AI TRADE TERMINAL // QUANTUM INSTITUTIONAL SUITE &bull; v5.4-PROD</div>
            <div className="text-slate-600">ZERO-KNOWLEDGE AUTH &bull; REVERSE PROXY PROTECTED</div>
          </div>
        </footer>
      )}
    </div>
  );
}

export { App };
